import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { FormError } from '@/components/ui/FormLayout'
import { InfoBanner } from '@/components/ui/InfoBanner'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { TextField } from '@/components/ui/TextField'
import { useToast } from '@/components/ui/Toast'
import { CheckIcon, InfoIcon, UserPlusIcon } from '@/components/ui/icons'
import { useInviteAt } from '@/features/admin/hooks/useStaffActions'
import { invitableRoles } from '@/features/admin/team'
import { useAuth } from '@/features/auth/authContext'
import { useCreateInvitation } from '@/features/invitations/hooks/useInvitationActions'
import { useInvitationRoles } from '@/features/invitations/hooks/useInvitationRoles'
import { invitationLink } from '@/features/invitations/invitationLink'
import { type InviteInput, inviteSchema } from '@/features/invitations/schemas/invitationSchemas'
import type { InvitationCreated } from '@/features/invitations/types'

import form from '@/components/ui/FormLayout/formLayout.module.css'

const FORM_ID = 'invite-user-form'

const EMPTY: InviteInput = { email: '', fullName: '', role: '', node: '' }

/** Request field names that match a field on this form. */
const FORM_FIELDS = new Set<keyof InviteInput>(['email', 'fullName', 'role'])

interface NodeOption {
  publicId: string
  name: string
}

interface InviteUserModalProps {
  open: boolean
  onClose: () => void
  /**
   * The franchises and Standorte to invite into (admin panels). Without it the
   * invitation goes to the active Standort, as on Einstellungen.
   */
  nodes?: { franchises: NodeOption[]; locations: NodeOption[] }
  /** Role keys a Standort's switches refuse, so the form never offers them there. */
  deniedRoles?: (locationId: string) => string[]
}

export function InviteUserModal({ open, onClose, nodes, deniedRoles }: InviteUserModalProps) {
  const { location, session } = useAuth()
  const roles = useInvitationRoles()
  // On the active Standort its switches apply; the admin panels apply each Standort's own.
  const roleOptions = nodes
    ? roles.data
    : roles.data && invitableRoles(roles.data, session?.switches)
  const createInvitation = useCreateInvitation()
  const inviteAt = useInviteAt()
  const { showToast } = useToast()
  const [created, setCreated] = useState<InvitationCreated | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<InviteInput>({ resolver: zodResolver(inviteSchema), defaultValues: EMPTY })

  const chosenRole = useWatch({ control, name: 'role' })
  const tier = roles.data?.find((role) => role.key === chosenRole)?.tier ?? 'location'
  const nodeOptions = nodes ? (tier === 'franchise' ? nodes.franchises : nodes.locations) : []

  // Closing forgets the link and the form, so the next opening starts clean.
  const close = () => {
    reset(EMPTY)
    setCreated(null)
    onClose()
  }

  const submit = handleSubmit(async (values) => {
    if (nodes && !values.node) {
      setError('node', {
        message:
          tier === 'franchise'
            ? 'Bitte wählen Sie eine Franchise.'
            : 'Bitte wählen Sie einen Standort.',
      })
      return
    }
    if (nodes && values.node && deniedRoles?.(values.node).includes(values.role)) {
      setError('role', { message: 'Diese Rolle ist an diesem Standort gesperrt.' })
      return
    }
    if (!nodes && !location) {
      setError('root', { message: 'Bitte wählen Sie zuerst einen Standort.' })
      return
    }
    try {
      if (nodes && values.node) {
        const node = tier === 'franchise' ? { franchise: values.node } : { location: values.node }
        setCreated(await inviteAt.mutateAsync({ input: values, node }))
      } else if (location) {
        setCreated(
          await createInvitation.mutateAsync({
            input: values,
            locationPublicId: location.publicId,
          }),
        )
      }
    } catch (error) {
      const apiError = ApiError.from(error)

      for (const field of Object.keys(apiError.fieldErrors)) {
        if (FORM_FIELDS.has(field as keyof InviteInput)) {
          setError(field as keyof InviteInput, { message: 'Bitte prüfen Sie dieses Feld.' })
        }
      }

      if ('franchise' in apiError.fieldErrors || 'location' in apiError.fieldErrors) {
        if (nodes) setError('node', { message: 'Bitte prüfen Sie diese Auswahl.' })
      }

      if (apiError.code === 'EMAIL_TAKEN') {
        setError('email', { message: 'Für diese E-Mail-Adresse besteht bereits ein Konto.' })
      }

      setError('root', {
        message:
          apiError.code === 'INVITATION_PENDING'
            ? 'Diese Einladung wird gerade erstellt. Bitte senden Sie sie nicht erneut.'
            : apiError.message,
      })
    }
  })

  const link = created ? invitationLink(created.token) : ''

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      showToast({ title: 'Link kopiert' })
    } catch {
      // No clipboard outside a secure context; the field stays selectable instead.
      showToast({
        title: 'Kopieren nicht möglich',
        description: 'Bitte markieren und kopieren Sie den Link manuell.',
        tone: 'error',
      })
    }
  }

  const expiresAt = created
    ? new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: location?.timezone,
      }).format(new Date(created.expiresAt))
    : ''

  return (
    <Modal
      open={open}
      onClose={close}
      title={nodes ? 'Person einladen' : 'Mitarbeiter einladen'}
      subtitle={
        nodes
          ? 'Rolle und Franchise oder Standort wählen'
          : location
            ? `Für ${location.name}`
            : 'Für Ihren aktuellen Standort'
      }
      size="sm"
      footer={
        created ? (
          <>
            <Button variant="ghost" onClick={close}>
              Fertig
            </Button>
            <Button leadingIcon={<CheckIcon />} onClick={() => void copy()}>
              Link kopieren
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              isLoading={isSubmitting}
              leadingIcon={<UserPlusIcon />}
            >
              Einladung erstellen
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className={form.grid}>
          <InfoBanner className={form.full} icon={<InfoIcon />}>
            Der Link wird nur jetzt angezeigt. Kopieren Sie ihn und geben Sie ihn an{' '}
            {created.fullName} weiter. Gültig bis {expiresAt}.
          </InfoBanner>

          <TextField
            className={form.full}
            label="Einladungslink"
            value={link}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : (
        <form id={FORM_ID} className={form.grid} onSubmit={submit} noValidate>
          <FormError message={errors.root?.message} />

          <TextField
            className={form.full}
            label="Name"
            placeholder="Vor- und Nachname"
            autoComplete="off"
            error={errors.fullName?.message}
            {...register('fullName')}
          />

          <TextField
            className={form.full}
            label="E-Mail"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            error={errors.email?.message}
            {...register('email')}
          />

          <Select
            className={form.full}
            label="Rolle"
            disabled={!roles.data}
            error={roles.isError ? 'Rollen konnten nicht geladen werden.' : errors.role?.message}
            {...register('role')}
          >
            <option value="">{roles.isPending ? 'Wird geladen …' : 'Bitte wählen'}</option>
            {roleOptions?.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label}
              </option>
            ))}
          </Select>

          {nodes && (
            <Select
              className={form.full}
              label={tier === 'franchise' ? 'Franchise' : 'Standort'}
              disabled={!chosenRole}
              error={errors.node?.message}
              {...register('node')}
            >
              <option value="">{chosenRole ? 'Bitte wählen' : 'Zuerst eine Rolle wählen'}</option>
              {nodeOptions.map((node) => (
                <option
                  key={node.publicId}
                  value={node.publicId}
                  disabled={deniedRoles?.(node.publicId).includes(chosenRole)}
                >
                  {node.name}
                  {deniedRoles?.(node.publicId).includes(chosenRole) ? ' (gesperrt)' : ''}
                </option>
              ))}
            </Select>
          )}
        </form>
      )}
    </Modal>
  )
}
